import { useState } from "react";
import { Link } from "react-router-dom";
import { LuArrowLeft, LuUserPlus, LuFileText, LuCreditCard, LuExternalLink, LuHelpCircle, LuArrowDown, LuLandmark, LuTicket, LuBadgeCheck, LuSmartphone, LuAlertTriangle, LuCopy, LuCheck, LuMail } from "react-icons/lu";
import "../../styles/infoscription/registration.css";

import { getNumericYear } from "../../utils/date";


const IBAN = "FR76 1090 7007 2123 0211 4852 090";
const MEMBERSHIP_EMAIL = "adhesion@vblc.fr";

//TARIFS DES LICENCES
//"birthYearOffsets" : écart avec l'année en cours (ex. 17 = né il y a 17 ans), recalculé automatiquement chaque année
//Pour afficher / masquer une licence, passer "visible" à true / false
const LICENCES = [
  { category: "Compétition Senior", detail: "Licence CompetLib (BLVB)", seniorFromOffset: 21, vblcPart: 100, ffvbPart: 30, visible: true },
  { category: "Compétition Senior", seniorFromOffset: 21, vblcPart: 80, ffvbPart: 100, visible: false },
  { category: "Compétition M21", birthYearOffsets: [20, 19, 18], vblcPart: 80, ffvbPart: 110, visible: true },
  { category: "Compétition M18", birthYearOffsets: [17, 16, 15], vblcPart: 80, ffvbPart: 110, visible: true },
  { category: "Compétition M15", birthYearOffsets: [14, 13], vblcPart: 80, ffvbPart: 75, visible: true },
  { category: "Compétition M13 / M11", birthYearOffsets: [12, 11, 10, 9], vblcPart: 80, ffvbPart: 70, visible: true },
];


function Registration() {

  const currentYear = getNumericYear(new Date());
  const [ibanCopied, setIbanCopied] = useState(false);

  const getBirthYears = (licence) => licence.seniorFromOffset
    ? `${currentYear - licence.seniorFromOffset} et avant`
    : licence.birthYearOffsets.map((offset) => currentYear - offset).join(", ");

  //COPIER L'IBAN DANS LE PRESSE-PAPIERS
  const copyIban = async () => {
    try {
      await navigator.clipboard.writeText(IBAN.replace(/\s/g, ""));
      setIbanCopied(true);
      setTimeout(() => setIbanCopied(false), 2500);
    } catch (error) {
      setIbanCopied(false);
    }
  };


  const steps = [
    {
      icon: LuUserPlus,
      title: "Créez votre compte FFVB",
      text: "Créez un compte ou connectez-vous à votre espace sur le site de la FFVB, puis suivez les étapes.",
      actions: <>
        <a className="btn btn--secondary" href="https://login.ffvolley.org/" target="_blank" rel="noopener noreferrer">
          Espace FFVB <LuExternalLink aria-hidden="true" /><span className="sr-only">(nouvel onglet)</span>
        </a>
        <Link className="link-arrow" to="/registrationHelp"><LuHelpCircle aria-hidden="true" /> Besoin d’aide ?</Link>
      </>,
    },
    {
      icon: LuFileText,
      title: "Complétez la fiche du club",
      text: "Remplissez en ligne la fiche d’adhésion du VBLC.",
      actions: <a className="btn btn--secondary" href="https://www.vblc.fr/dolivblc/custom/adhesion/public/index.php?mainmenu=home" target="_blank" rel="noopener noreferrer">
        Fiche du club VBLC <LuExternalLink aria-hidden="true" /><span className="sr-only">(nouvel onglet)</span>
      </a>,
    },
    {
      icon: LuCreditCard,
      title: "Réglez votre licence",
      text: "Payez votre licence pour valider votre inscription pour la saison à venir.",
      actions: <a className="link-arrow" href="#tarifs">Voir les tarifs <LuArrowDown aria-hidden="true" /></a>,
    },
  ];


  return (
    <main className="registration">

      {/* EN-TÊTE */}
      <header className="page-hero">
        <div className="wrap page-hero_content">
          <Link to="/infoscription" className="page-hero_back"><LuArrowLeft aria-hidden="true" /> Informations</Link>
          <p className="page-hero_eyebrow">
            <span className="tricolor" aria-hidden="true"><span></span><span></span><span></span></span>
            Inscriptions
          </p>
          <h1 className="page-hero_title">Rejoindre le VBLC</h1>
          <p className="page-hero_text">Pour vous inscrire ou renouveler votre licence, rien de plus simple : trois étapes, entièrement en ligne.</p>
        </div>
      </header>

      {/* LES 3 ÉTAPES */}
      <div className="wrap">
        <ol className="steps surface">
          {steps.map(({ icon: Icon, title, text, actions }, index) => (
            <li key={title} className="step">
              <div className="step_top">
                <span className="step_number">{index + 1}</span>
                <span className="icon-tile icon-tile--red" aria-hidden="true"><Icon /></span>
              </div>
              <h2 className="title-md">{title}</h2>
              <p className="step_text">{text}</p>
              <div className="step_actions">{actions}</div>
            </li>
          ))}
        </ol>
      </div>

      {/* TARIFS */}
      <section id="tarifs" className="section" aria-labelledby="prices-title">
        <div className="wrap">
          <div className="section_header">
            <div className="section_heading">
              <p className="eyebrow">Tarifs</p>
              <h2 id="prices-title" className="title-xl">Cotisation et licences</h2>
              <p className="section_intro">Le montant total comprend la part du club et la part reversée aux instances du volley-ball (FFVB, ligue et comité).</p>
            </div>
          </div>

          <div className="surface prices_panel">
            <table className="prices_table">
              <thead>
                <tr>
                  <th scope="col">Licence</th>
                  <th scope="col">Année de naissance</th>
                  <th scope="col" className="is-number">Part VBLC</th>
                  <th scope="col" className="is-number">Part FFVB, LNAVB, CDVB64</th>
                  <th scope="col" className="is-number">Total</th>
                </tr>
              </thead>
              <tbody>
                {LICENCES.filter((licence) => licence.visible).map((licence) => (
                  <tr key={licence.category + (licence.detail || "")}>
                    <th scope="row">
                      <span className="prices_category">{licence.category}</span>
                      {licence.detail && <span className="prices_detail">{licence.detail}</span>}
                    </th>
                    <td data-label="Année de naissance">{getBirthYears(licence)}</td>
                    <td data-label="Part VBLC" className="is-number">{licence.vblcPart} €</td>
                    <td data-label="Part FFVB, LNAVB, CDVB64" className="is-number">{licence.ffvbPart} €</td>
                    <td data-label="Total" className="is-number prices_total">{licence.vblcPart + licence.ffvbPart} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* RÈGLEMENT */}
      <section id="paiement" className="section section--white" aria-labelledby="payment-title">
        <div className="wrap">
          <div className="section_header">
            <div className="section_heading">
              <p className="eyebrow">Paiement</p>
              <h2 id="payment-title" className="title-xl">Règlement de la licence</h2>
            </div>
          </div>

          <ul className="payment_grid">
            <li className="payment_card">
              <span className="icon-tile icon-tile--ink" aria-hidden="true"><LuLandmark /></span>
              <h3 className="title-md">Par virement</h3>
              <p>Indiquez votre nom et prénom, ou ceux de votre enfant, dans le libellé du virement.</p>
              <div className="iban">
                <span className="iban_label">IBAN</span>
                <code className="iban_value">{IBAN}</code>
                <button className="iban_copy" onClick={copyIban} aria-label="Copier l’IBAN">
                  {ibanCopied ? <><LuCheck aria-hidden="true" /> Copié</> : <><LuCopy aria-hidden="true" /> Copier</>}
                </button>
              </div>
            </li>

            <li className="payment_card">
              <span className="icon-tile icon-tile--green" aria-hidden="true"><LuBadgeCheck /></span>
              <h3 className="title-md">Avec le Pass’Sport</h3>
              <p>Le Pass’Sport est reconduit pour l’année 2026/2027. D’un montant de 50 €, il concerne les jeunes de 6 à 17 ans dont le quotient familial (CAF ou MSA) est inférieur ou égal à 699 €.</p>
              <p>Envoyez votre code Pass’Sport à <a href={`mailto:${MEMBERSHIP_EMAIL}`}>{MEMBERSHIP_EMAIL}</a> et complétez le reste à payer avec un autre mode de paiement si besoin.</p>
              <a className="link-arrow payment_card_link" href="https://pass.sports.gouv.fr/" target="_blank" rel="noopener noreferrer">
                En savoir plus sur le Pass’Sport <LuExternalLink aria-hidden="true" /><span className="sr-only">(nouvel onglet)</span>
              </a>
            </li>

            <li className="payment_card">
              <span className="icon-tile icon-tile--sand" aria-hidden="true"><LuTicket /></span>
              <h3 className="title-md">Avec les coupons sport ANCV</h3>
              <p>Vous pouvez compléter le reste à payer avec un autre mode de paiement si besoin.</p>
            </li>

            <li className="payment_card">
              <span className="icon-tile icon-tile--red" aria-hidden="true"><LuSmartphone /></span>
              <h3 className="title-md">Avec l’application Lydia</h3>
              <p>Paiement possible depuis votre téléphone avec l’application Lydia.</p>
            </li>
          </ul>

          <div className="msg msg--warning payment_warning" role="note">
            <LuAlertTriangle aria-hidden="true" />
            <p>Pour des raisons d’assurance, l’accès aux entraînements et à la compétition au sein des équipes du VBLC n’est autorisé que si le dossier d’inscription est complet et la cotisation réglée.</p>
          </div>

          <p className="payment_contact">
            <LuMail aria-hidden="true" /> Une question sur votre inscription ? Écrivez-nous à <a href={`mailto:${MEMBERSHIP_EMAIL}`}>{MEMBERSHIP_EMAIL}</a>
          </p>
        </div>
      </section>
    </main>
  )
};

export default Registration;
